import React, { createContext, useContext, ReactNode } from 'react';
import { FlamecoAuthProvider, useFlamecoAuth } from '../flameco/FlamecoAuthProvider';
import type { FlamecoUser } from '../flameco/flameco-auth';
import { FLAMECO_AUTH_URL, FLAMECO_CLIENT_ID } from '../constants/flamecoAuth';

// =============================================================================
// Sign-in is the Flameco Authentication Service (auth.flameco.com) only.
// People sign in on its hosted page; this context exposes the signed-in person
// in the shape the rest of the app already reads (user.email for "created by",
// flamecoUser for the employee id and role). Access to each tool is a tag set
// by an administrator in the auth console (see flameco-auth.json).
// =============================================================================

export type AuthSource = 'flameco';

/** The bits of the signed-in person the app reads. */
export interface AuthUser {
  email: string | null;
  name: string;
  employeeId: string;
}

interface AuthContextValue {
  authSource: AuthSource | null;
  isAuthenticated: boolean;
  loading: boolean;
  /** Signs out of this app. `everywhere` also ends the Flameco session in every app on this device. */
  signOut: (o?: { everywhere?: boolean }) => Promise<void>;
  user: AuthUser | null;
  flamecoUser: FlamecoUser | null;
  /** True when the signed-in person has this tag (feature) in Internal Tools. */
  can: (tag: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function AuthBridge({ children }: { children: ReactNode }) {
  const { ready, user, can, signOut } = useFlamecoAuth();
  const value: AuthContextValue = {
    authSource: user ? 'flameco' : null,
    isAuthenticated: !!user,
    loading: !ready,
    signOut: (o) => signOut(o),
    user: user ? { email: user.email, name: user.name, employeeId: user.employeeId } : null,
    flamecoUser: user,
    can,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <FlamecoAuthProvider options={{ authUrl: FLAMECO_AUTH_URL, clientId: FLAMECO_CLIENT_ID }}>
    <AuthBridge>{children}</AuthBridge>
  </FlamecoAuthProvider>
);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
